import { useEffect } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
    PASTE_COMMAND,
    DROP_COMMAND,
    COMMAND_PRIORITY_LOW,
    $getSelection,
    $isRangeSelection,
    $getRoot,
} from "lexical";
import { $createImageNode } from "../nodes/ImageNode";

export default function ImagePasteDropPlugin() {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        const readAsDataURL = (file) =>
            new Promise((resolve, reject) => {
                const r = new FileReader();
                r.onload = () => resolve(r.result);
                r.onerror = reject;
                r.readAsDataURL(file);
            });

        const extractFiles = (dataTransfer) => {
            const files = Array.from(dataTransfer?.files || []);
            if (files.length) return files;
            const items = Array.from(dataTransfer?.items || []);
            return items
                .map((it) => (it.kind === "file" ? it.getAsFile() : null))
                .filter(Boolean);
        };

        const isExcelClipboard = (clipboardData) => {
            const html = clipboardData?.getData("text/html") ?? "";
            return (
                html.includes("schemas.microsoft.com/office/excel") ||
                html.includes("urn:schemas-microsoft-com:office:excel")
            );
        };

        const insertFiles = async (files) => {
            for (const f of files) {
                if (!f || !f.type.startsWith("image/")) continue;
                const dataUrl = await readAsDataURL(f);
                editor.update(() => {
                    const node = $createImageNode(String(dataUrl), f.name || "");
                    const selection = $getSelection();
                    if ($isRangeSelection(selection)) {
                        selection.insertNodes([node]);
                    } else {
                        $getRoot().append(node);
                    }
                });
            }
        };

        const unregisterPaste = editor.registerCommand(
            PASTE_COMMAND,
            (event) => {
                // Excel puts a bitmap on the clipboard alongside its HTML table.
                // Let TablePastePlugin handle Excel pastes � don't treat the
                // accompanying image as a user-intentioned image paste.
                if (isExcelClipboard(event.clipboardData)) return false;

                const files = extractFiles(event.clipboardData);
                if (!files.some((f) => f && f.type?.startsWith("image/"))) return false;

                event.preventDefault();
                insertFiles(files);
                return true;
            },
            COMMAND_PRIORITY_LOW
        );

        const unregisterDrop = editor.registerCommand(
            DROP_COMMAND,
            (event) => {
                const files = extractFiles(event.dataTransfer);
                if (!files.some((f) => f && f.type?.startsWith("image/"))) return false;

                event.preventDefault();
                insertFiles(files);
                return true;
            },
            COMMAND_PRIORITY_LOW
        );

        return () => {
            unregisterPaste();
            unregisterDrop();
        };
    }, [editor]);

    return null;
}